import type { CSSProperties } from 'react';
import {
  hexToHsl,
  hslStr,
  lighten,
  darken,
  autoContrastText,
} from '@core/tenant/useRestaurantTheme';
import type { RestaurantGroup } from '../api/restaurantGroups';

/**
 * Build design-system CSS variable overrides from a group's configured colors.
 *
 * Returns {} when the group sets no background color (page keeps the default
 * design-system theme). Does NOT set --primary: the brand/CTA color stays
 * as defined by the design system.
 *
 * Color resolution order:
 *   bg  = groupBackgroundColor ?? mainColor
 *   fg  = groupTextColor ?? mainColorText ?? autoContrastText(bg)
 *
 * Token derivation:
 *   --background     = hslStr(bg)
 *   --foreground     = hslStr(fg)
 *   --card           = isDark ? lighten(bg, 6) : darken(bg, 3)
 *   --card-foreground= hslStr(fg)
 *   --border         = isDark ? lighten(bg, 14) : darken(bg, 8)
 *   --muted          = isDark ? lighten(bg, 8)  : darken(bg, 4)
 *   --muted-foreground= isDark ? darken(fg, 35) : lighten(fg, 35)
 *     (lighten does not accept negative amounts, so muted-fg uses darken for
 *      the dark-theme case to produce a subdued, readable foreground variant)
 */
export function buildGroupThemeVars(
  group: Pick<
    RestaurantGroup,
    'mainColor' | 'mainColorText' | 'groupBackgroundColor' | 'groupTextColor'
  >,
): CSSProperties {
  const bg = group.groupBackgroundColor || group.mainColor;
  if (!bg) return {};

  const fg =
    group.groupTextColor ||
    group.mainColorText ||
    autoContrastText(bg);

  // Match autoContrastText's boundary: it treats a background as "dark" (and
  // picks light text) when lightness <= 55. The surface derivations below must
  // use the SAME threshold, otherwise a mid-gray bg (L in 50..55) with no
  // configured text color gets a light auto-foreground while the card/border
  // derivations go the "light theme" direction — producing inconsistent,
  // low-contrast surfaces.
  const { l: bgL } = hexToHsl(bg);
  const isDark = bgL <= 55;

  return {
    ['--background' as string]: hslStr(bg),
    ['--foreground' as string]: hslStr(fg),
    ['--card' as string]: isDark ? lighten(bg, 6) : darken(bg, 3),
    ['--card-foreground' as string]: hslStr(fg),
    ['--border' as string]: isDark ? lighten(bg, 14) : darken(bg, 8),
    ['--muted' as string]: isDark ? lighten(bg, 8) : darken(bg, 4),
    // lighten() doesn't accept negative amounts; for the dark-theme case we
    // darken the foreground to get a subdued muted-foreground variant.
    ['--muted-foreground' as string]: isDark ? darken(fg, 35) : lighten(fg, 35),
  } as CSSProperties;
}
